import { createPortal } from 'react-dom';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import { useIsMobile } from '../_custom/Hooks/useIsMobile';
import {
    ALL_KNOWN_DETAILS_DRAWER_WIDTH,
    ALL_KNOWN_DETAILS_TESTIDS,
    ALL_KNOWN_DETAILS_TITLE,
    EMPTY_TAB_COPY,
    KNOWN_DETAILS_PANEL_ID,
} from './constants';
import { entitiesForType } from './utils/allKnownDetailsUtils';
import EntityAccordion from './components/EntityAccordion/EntityAccordion';
import KnownDetailsTabBar, { tabIdFor } from './components/KnownDetailsTabBar/KnownDetailsTabBar';
import type { AllKnownDetailsData, KnownDetailType } from './types';
import styles from './AllKnownDetailsDrawer.module.scss';

interface AllKnownDetailsDrawerProps {
    data: AllKnownDetailsData | null;
    activeType: KnownDetailType;
    onTypeChange: (type: KnownDetailType) => void;
    onClose: () => void;
}

const AllKnownDetailsDrawer = ({ data, activeType, onTypeChange, onClose }: AllKnownDetailsDrawerProps) => {
    const isMobile = useIsMobile();
    const entities = entitiesForType(data, activeType);

    return createPortal(
        <SideDrawer
            heading={ALL_KNOWN_DETAILS_TITLE}
            showHeading={false}
            allowClose
            width={isMobile ? '100%' : ALL_KNOWN_DETAILS_DRAWER_WIDTH}
            // Square top, matching every other bottom sheet in the app; SideDrawer rounds its mobile sheet by default.
            style={{ borderRadius: 0 }}
            onClose={onClose}
        >
            <div className={styles.Drawer} data-testid={ALL_KNOWN_DETAILS_TESTIDS.DRAWER}>
                <KnownDetailsTabBar data={data} activeType={activeType} onSelect={onTypeChange} />
                <div
                    id={KNOWN_DETAILS_PANEL_ID}
                    role="tabpanel"
                    aria-labelledby={tabIdFor(activeType)}
                    className={styles.Panel}
                    data-testid={ALL_KNOWN_DETAILS_TESTIDS.TAB_PANEL}
                >
                    {entities.length ? (
                        entities.map((entity) => (
                            <EntityAccordion key={`${entity.entityId}-${activeType}`} entity={entity} type={activeType} />
                        ))
                    ) : (
                        <div className={styles.EmptyState} data-testid={ALL_KNOWN_DETAILS_TESTIDS.EMPTY_TAB}>
                            {EMPTY_TAB_COPY[activeType]}
                        </div>
                    )}
                </div>
            </div>
        </SideDrawer>,
        document.body,
    );
};

export default AllKnownDetailsDrawer;
